import { FC, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import { SearchableSelectArray, SearchableSelectArrayOption, SearchableSelectArrayOptionGroup } from 'components';
import { useSizeTypes } from 'contexts';
import { BookingDetails, EquipmentType, SIZE_TYPE_COMBOS } from 'types';

import { useNewBooking } from './new-booking';

const DetailRow: FC<{ details: BookingDetails; index: number }> = ({ details, index }) => {
  const { pathname } = useLocation();

  const { booking, setDetails } = useNewBooking();
  const { containerSizeTypeEntries, chassisSizeTypeEntries, gensetSizeTypeEntries, getEquipmentType } = useSizeTypes();

  const isNewBooking = useMemo(() => pathname.includes('bookings/new'), [pathname]);

  const currentSizeTypes: Set<string> = useMemo(() => {
    return Object.assign([], booking.bookingDetails).reduce((set, { iso }) => set.add(iso), new Set<string>());
  }, [booking.bookingDetails]);

  /* booking accept-list dropdown — container group carries the FE-only combo overlay */
  const containerEntries = useMemo(
    () => [...containerSizeTypeEntries, ...SIZE_TYPE_COMBOS],
    [containerSizeTypeEntries]
  );

  const sizeTypeOptions: SearchableSelectArrayOptionGroup[] = useMemo(() => {
    const shouldInclude = ([key, val]: [string, string[]]): boolean =>
      (val && !val.some(st => currentSizeTypes.has(st))) || (details.iso ?? []).some(st => val.includes(st));
    const toOption = ([key, val]: [string, string[]]): SearchableSelectArrayOption => ({ value: val, label: key });
    return [
      { groupLabel: 'CONTAINER', value: containerEntries.filter(shouldInclude).map(toOption) },
      { groupLabel: 'CHASSIS', value: chassisSizeTypeEntries.filter(shouldInclude).map(toOption) },
      { groupLabel: 'GENSET', value: gensetSizeTypeEntries.filter(shouldInclude).map(toOption) }
    ];
  }, [currentSizeTypes, details.iso, containerEntries, chassisSizeTypeEntries, gensetSizeTypeEntries]);

  const handleChange = useCallback(
    (partial: Partial<BookingDetails>) => {
      setDetails({ ...details, ...partial }, index);
    },
    [details, index, setDetails]
  );

  const handleDelete = useCallback(() => {
    setDetails(undefined, index);
  }, [index, setDetails]);

  return (
    <div className="grid-5 p-2">
      <div>
        <h6 className="text-info-dark">SIZE TYPE</h6>
        <div className="mt-2 w-100">
          <SearchableSelectArray
            onValueSelected={iso => handleChange({ iso })}
            options={sizeTypeOptions}
            value={
              [
                ['', ['']] as [string, string[]],
                ...chassisSizeTypeEntries,
                ...containerEntries,
                ...gensetSizeTypeEntries
              ].find(st => details.iso?.join() === st[1]?.join())?.[0] ??
              /* retired/inactive code(s) have no active label — show the raw code(s) (zero-churn) */
              details.iso?.join('/')
            }
            required={isNewBooking}
            data-cy={`booking-detail-sizetype-search-${index}`}
          />
        </div>
      </div>
      <div>
        <h6 className="text-info-dark">QUANTITY</h6>
        <input
          type="number"
          className="mt-2 w-100"
          onChange={e => handleChange({ count: Math.max(0, Number(e.target.value) || 0) })}
          value={details.count ?? ''}
          required={isNewBooking}
          data-cy={`booking-detail-quantity-input-${index}`}
        />
      </div>
      <div>
        <h6 className="text-info-dark">RECEIVED</h6>
        <input type="number" className="mt-2 w-100" defaultValue={0} disabled />
      </div>
      <div>
        <h6 className="text-info-dark">DELIVERED</h6>
        <input type="number" className="mt-2 w-100" defaultValue={0} disabled />
      </div>
      <div className="grid-2 h-100">
        {details?.iso &&
        (details.iso.some(st => getEquipmentType(st) === EquipmentType.GENSET) ||
          details.iso.some(st => st.endsWith('RF'))) ? (
          <div className="carb-checkbox">
            <h6 className="text-info-dark">CARB</h6>
            <div className="mt-2 p-2">
              <input
                className="w-100"
                type="checkbox"
                checked={!!details?.carb}
                onChange={e => handleChange({ carb: e.target.checked })}
              />
            </div>
          </div>
        ) : null}
        <div className="flex-fill d-flex align-items-end h-100">
          <button
            className="btn-back p-2"
            type="button"
            onClick={handleDelete}
            disabled={(booking.bookingDetails?.length ?? 0) <= 1}
          >
            <span className="material-icons icon-xlarge">delete</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const NewEquipment: FC = () => {
  const { booking, setDetails } = useNewBooking();

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>EQUIPMENT DETAILS</h4>
      </div>
      <div className="card-body">
        {Object.assign([], booking.bookingDetails).map((details, index) => (
          <DetailRow details={details} index={index} key={index} />
        ))}
        <div className="p-2">
          <button
            className="btn-outline"
            type="button"
            onClick={() => setDetails({ carb: false, count: 1, iso: [] } as BookingDetails)}
            data-cy="add-equipment-button"
          >
            ADD EQUIPMENT
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewEquipment;
